import { useState } from "react";

import {
  Button,
  CloseButton,
  Dialog,
  EmptyState,
  HStack,
  IconButton,
  Image,
  Input,
  Menu,
  Popover,
  Portal,
  Table,
} from "@chakra-ui/react";
import { format } from "date-fns";
import moment from "moment";
import { useForm, Controller } from "react-hook-form";
import {
  LuDelete,
  LuMoreVertical,
  LuPen,
  LuPlus,
  LuTrash,
  LuX,
} from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";
import useAlert from "src/hooks/use-alert";
import FileDropZone from "src/components/FileDropZone";
import useDurationChecker, { Timeline } from "src/hooks/use-duration-checker";
import { useGiveOfferMutation } from "src/lib/endpoints/hire/hire.api";
import { JobDetail } from "src/lib/endpoints/hire/hire.type";
import DateTime from "src/lib/utils/date-time";
import { commaSeperatedNumberFormat } from "src/lib/utils/format-text";
import CustomAlertDialog from "../Alert/custom-alert-dialog";
import OfferActions from "./OfferActions";
import Field from "../field";
import MakeDepositModal from "../Modal/MakeDeposit";
import Status from "../Status/Status";
import {
  OfferForm,
  OfferMilestonePayload,
} from "../../pages/ProjectSetup/types/OfferObject";
import { useTypedSelector } from "src/lib/store/store";
import { selectUserState } from "src/lib/features/auth/authSlice";

enum FieldsName {
  MILESTONE = "milestone",
  FILE = 'file',
  AMOUNT = "amount",
  START_DATE = "start_date",
  // END_DATE = "end_date",
  DURATION = "duration",
}

interface InputFields {
  milestone: string;
  file?: any;
  amount: string;
  start_date: string;
  duration: string;
}

type Props = {
  currencySymbol: string;
  duration: number;
  projectBudget: number;
  description: string;
  jobDetail: JobDetail;
  isBasic?: boolean;
  onDone: (data: OfferForm) => void;
};

const CreateMilestone = ({
  currencySymbol,
  description,
  projectBudget,
  duration,
  jobDetail,
  isBasic,
  onDone,
}: Props) => {
  const { jobId, userId } = useParams();
   const {user} = useTypedSelector(selectUserState);


    //  const { user } = useTypedSelector(selectUserState);
    //  const userId = user?.id ?? ''
  const { giveOffer, isLoading } = useGiveOfferMutation({
    jobId: jobId ?? "",
    userId: userId ?? "",
  });
  const [depositOpen, setDepositOpen] = useState(false);
  const navigate = useNavigate();
  const [balance, setBalance] = useState("0");
  const [percentage, setPercentage] = useState("0");
  const [requiresDeposit, setRequiresDeposit] = useState(false);  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setIsOpen] = useState(false);
  const [paymentGatewayUrl, setPaymentGatewayUrl] = useState("");
  const [transactionId, setTransactionId] = useState("");


  const [tableData, setTableData] = useState<Timeline[]>([]);
  const { timelineValidation, sortTimelines, costValidation } =
    useDurationChecker(tableData, +duration, projectBudget);

  const [openAddMilstoneDialog, setAddMilstoneDialog] = useState(false);

  const {
    state: alertState,
    open: openAlert,
    close: closeAlert,
    Alert,
  } = useAlert();

  const [editMilestone, setEditMilestone] = useState<number>();

  const handleDelete = (idx: number) => {
    setTableData((prev) => prev.filter((_, i) => i !== idx));
  };

  const createOffer = () => {
    if (timelineValidation !== undefined) {
      openAlert({
        ...alertState,
        title:
          timelineValidation === "invalid"
            ? "Invalid date selection"
            : "Duration in days does not align with given duration",
        variant: "error",
        active: true,
      });
      return;
    }

    let milestones: OfferMilestonePayload[] = tableData.map((each, i) => {
      return {
        amountPayable: each.amountPayable,
        description: each.description,
        startDate: each.start_date.replace("Z", ""),
        endDate: new Date(
          moment(each.start_date)
            .add(+each.duration + 1, "day")
            .toDate()
        )
          .toISOString()
          .replace("Z", ""),
        job_detail_id: jobId,
        file: each.file,
      //  duration: each.duration,
      };
    });

    const total = milestones.reduce(
      (total, value) => total + parseInt(value.amountPayable),
      0
    );

    const budget = parseFloat(projectBudget?.toString());

    if (total > budget) {
      openAlert({
        ...alertState,
        title: `Total budget of ${total} exceeds Proposed budget of ${budget}`,
        variant: "info",
        active: true,
      });
    }

    const formData = new FormData();
    formData.append("description", description);
    formData.append("work_type", "fixedcost");
    formData.append("payment_type", "milestone-based");
    formData.append("agreed_total_cost", total.toString());
    formData.append("startDate", tableData[0].start_date.replace("Z", ""));
    formData.append("milestones", JSON.stringify(milestones));
    formData.append("retainer_agreed_interval", `${tableData.length}`);
    formData.append("agreed_duration", `${duration}`);

    if (isBasic) {
      giveOffer(formData, {
        onSuccess: async (res) => {
          if (typeof res?.data?.amount === "number") {
            openAlert({
              ...alertState,
              title: res?.message ?? "",
              variant: "info",
              active: true,
            });
            setBalance(parseFloat(res.data.amount).toFixed(2));
            setPercentage(parseFloat(res.data.percentage).toFixed(2));
            setRequiresDeposit(true);
          } else if (res?.status) {
            openAlert({
              ...alertState,
              title: res?.message ?? "Offer sent successfully",
              variant: "success",
              active: true,
            });
            setTimeout(() => {
                    navigate(`/project`);
              // navigate(`/project/${res?.data?.id ?? ""}`);
            }, 1500); 
          } else {
            openAlert({
              ...alertState,
              title: res?.message ?? "Failed to send offer",
              variant: "error",
              active: true,
            });
          }
        },
        onError: (error: any) => {
          openAlert({
            ...alertState,
            title: error?.response?.data?.message ?? error?.message ?? "",
            variant: "error",
            active: true,
          });
        },
      });
    } else {
      return onDone({
        description: description,
        work_type: "fixedcost",
        payment_type: "milestone-based",
        agreed_total_cost: total.toString(),
        startDate: tableData[0].start_date.replace("Z", ""),
        milestones: milestones,
        retainer_agreed_interval: tableData.length.toString(),
        agreed_duration: duration.toString(),
      });
    }

  };

  // const handleFundWallet = async () => {
  //   if (isSubmitting) return;
  //   setIsSubmitting(true);

  //   let formData = new FormData();
  //   formData.append("amount", balance.toString());
  //   formData.append("gateway", "paystack");

  //   await fundWallet(formData, {
  //     onSuccess: async (res) => {
  //       setIsSubmitting(false);
  //       setTransactionId(res?.transaction?.id);
  //       setPaymentGatewayUrl(res?.data?.authorization_url ?? "");
  //     },
  //     onError: (error: any) => {
  //       setIsSubmitting(false);
  //       openAlert({
  //         ...alertState,
  //         title: error?.response?.data?.message ?? error?.message ?? "",
  //         variant: "error",
  //         active: true,
  //       });
  //     },
  //   });
  // };

  const onNext = () => {
    // setIsOpen(true)
    createOffer();
  };

  if (!jobDetail) return null;

  return (
    <>
      <div>
        <div className="mb-6 mt-6 rounded-xl border p-4">
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-sm font-bold text-brand-navy">Milestones</h4>
              <button
                type="button"
                onClick={() => setAddMilstoneDialog(true)}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-brand-navy px-3 text-xs font-semibold text-brand-navy transition-colors hover:bg-surface"
              >
                <LuPlus className="h-3.5 w-3.5" />
                Add milestone
              </button>
            </div>

            {tableData.length == 0 && (
              <EmptyState.Root>
                <EmptyState.Content gap={4}>
                  <EmptyState.Indicator>
                    <Image
                      src="/illustrations/empty_list.svg"
                      h="20"
                      w="20"
                      alt="Empty list"
                    />
                  </EmptyState.Indicator>
                  <EmptyState.Title>No Milestone</EmptyState.Title>
                  <EmptyState.Description>
                    You have no milestones yet, add milestones to see them here.
                  </EmptyState.Description>
                </EmptyState.Content>
              </EmptyState.Root>
            )}

            {tableData.length > 0 && (
              <>
                <Table.ScrollArea mt={5}>
                  <Table.Root
                    // variant={"line"}
                    // borderWidth={"thin"}
                    color="fg"
                    interactive
                  >
                    <Table.Header>
                      <Table.Row borderBottomWidth={"thin"}>
                        <Table.ColumnHeader>Task</Table.ColumnHeader>
                        <Table.ColumnHeader>Amount</Table.ColumnHeader>
                        <Table.ColumnHeader>Start Date</Table.ColumnHeader>
                        <Table.ColumnHeader>End Date</Table.ColumnHeader>
                        <Table.ColumnHeader>Status</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {
                        //                <p className="max-w-40 break-word">{each?.description}</p>,
                        // <p>${commaSeperatedNumberFormat(each.amountPayable)}</p>,
                        // <p>{format(new Date(each?.start_date ?? Date.now()), "EEE, MMM d")}</p>,
                        // <p>
                        //   {format(
                        //     moment(each.start_date).add(each.duration, "day").toDate(),
                        //     "EEE, MMM d"
                        //   )}
                        // </p>,
                        // <Status label={"open"} />,
                        // <X onClick={() => handleDelete(idx)} />,
                        tableData.map((data, index) => {
                          return (
                            <Table.Row key={index} borderBottomWidth={"thin"}>

                              <Table.Cell><div className="flex flex-col gap-1">
                                {data.description}

           {data?.file &&                          
              <a
                  target="_blank"
                  className="underline text-blue-500 "
                  href={data?.file ?? ""}
                >
                  View  more
                </a>
                        }
                                 </div></Table.Cell>
                              <Table.Cell>
                 { user?.userCompany.currency_symbol  ?? '₦' }
                                {commaSeperatedNumberFormat(data.amountPayable)}
                              </Table.Cell>
                              <Table.Cell>
                                {data?.start_date ? format(new Date(data.start_date), "EEE, MMM d") : "—"}
                              </Table.Cell>
                              <Table.Cell>
                                {data?.start_date ? format(
                                  moment(data.start_date)
                                    .add(data.duration, "day")
                                    .toDate(),
                                  "EEE, MMM d"
                                ) : "—"}
                              </Table.Cell>
                              <Table.Cell>
                                <Status label={"open"} />
                              </Table.Cell>
                              <Table.Cell>
                                <Menu.Root
                                  onSelect={({ value }) => {
                                    switch (value) {
                                      case "edit":
                                        setEditMilestone(index);
                                        setAddMilstoneDialog(true);
                                        break;
                                      case "remove":
                                        handleDelete(index);
                                        break;
                                      default:
                                        break;
                                    }
                                  }}
                                >
                                  <Menu.Trigger asChild>
                                    <IconButton
                                      color={"fg"}
                                      size="sm"
                                      // onClick={() => handleDelete(index)}
                                      variant={"ghost"}
                                    >
                                      <LuMoreVertical />
                                    </IconButton>
                                  </Menu.Trigger>
                                  <Portal>
                                    <Menu.Positioner>
                                      <Menu.Content>
                                        <Menu.Item
                                          value="edit"
                                          cursor={"pointer"}
                                        >
                                          <LuPen />
                                          <Menu.ItemText>Edit</Menu.ItemText>
                                        </Menu.Item>
                                        <Menu.Item
                                          value="remove"
                                          cursor={"pointer"}
                                          _hover={{
                                            bg: "bg.error",
                                            color: "fg.error",
                                          }}
                                        >
                                          <LuTrash />
                                          <Menu.ItemText>Remove</Menu.ItemText>
                                        </Menu.Item>
                                      </Menu.Content>
                                    </Menu.Positioner>
                                  </Portal>
                                </Menu.Root>
                              </Table.Cell>
                            </Table.Row>
                          );
                        })
                      }
                    </Table.Body>
                  </Table.Root>
                </Table.ScrollArea>

                {timelineValidation === "overflow" && (
                  <p className="pt-4 text-xs font-medium text-red-600">
                    Duration in days does not align with given duration
                  </p>
                )}

                {timelineValidation === "invalid" && (
                  <p className="pt-4 text-xs font-medium text-red-600">
                    Invalid date selection
                  </p>
                )}

                {!costValidation && (
                  <p className="pt-4 text-xs font-medium text-red-600">
                    Project budget has exceeded actual budget
                  </p>
                )}
              </>
            )}
        </div>

        {tableData.length !== 0 && (
          <OfferActions
            requiresDeposit={requiresDeposit}
            percentage={percentage}
            currency={user?.userCompany?.currency ?? "₦"}
            balance={balance}
            onFundWallet={() => setDepositOpen(true)}
            fundLoading={isSubmitting}
            submitLabel={isBasic ? "Give Offer" : "Next"}
            onSubmit={isBasic ? createOffer : onNext}
            submitLoading={isLoading}
            submitDisabled={timelineValidation !== undefined}
          />
        )}

        {depositOpen && jobDetail && (
          <MakeDepositModal
            label="Confirm Your Deposit"
            info={`Proceed to make a payment of the displayed amount to the company account as shown below:`}
            close={() => setDepositOpen(false)}
            open={depositOpen}
            job={jobDetail}
            userId={userId}
            amount={balance}
            type="initial_deposit"
            loading={isLoading}
            confirmDeposit={() => setDepositOpen(false)}
          />
        )}

        {/* {paymentGatewayUrl !== "" && (
        <PaymentGatewayModal
          transactionId={transactionId}
          open={paymentGatewayUrl !== ""}
          close={() => {
            setPaymentGatewayUrl("");
          }}
          url={paymentGatewayUrl}
          callback={() => {
            setPaymentGatewayUrl("");
          }}
        />
      )} */}

        <CustomAlertDialog
          open={open}
          onOpenChange={setIsOpen}
          callback={createOffer}
          message="Ensure you have held virtual meeting with this talent before proceeding to give offer"
        />

        <span className="fixed right-3 top-3 z-30">
          <Alert />
        </span>
      </div>

      <AddMilstoneDialog
        edit={
          editMilestone != undefined ? tableData[editMilestone] : undefined
        }
        jobId={jobId ?? ""}
        open={openAddMilstoneDialog}
        onClose={() => setAddMilstoneDialog(false)}
        onAdd={(milestone) => {
          setTableData((prev) => {
            const list = [...prev];

            if (editMilestone != undefined) list[editMilestone] = milestone;
            else list.push(milestone);

            return sortTimelines(list);
          });
          if (editMilestone) setEditMilestone(undefined);
        }}
      />
    </>
  );
};

export default CreateMilestone;

function AddMilstoneDialog({
  edit,
  jobId,
  open,
  onClose,
  onAdd,
}: {
  edit?: Timeline;
  jobId: string;
  open: boolean;
  onClose: () => void;
  onAdd: (miletsone: Timeline) => void;
}) {
  const [minDate, setMinDate] = useState(DateTime.getCurrentDate());
  const formMethods = useForm<InputFields>({
    defaultValues: {
      [FieldsName.MILESTONE]: edit?.description ?? "",
      [FieldsName.FILE]: edit?.file ?? undefined,
      [FieldsName.AMOUNT]: edit?.amountPayable ?? "",
      [FieldsName.START_DATE]: edit?.start_date ?? "",
      [FieldsName.DURATION]: edit?.duration ?? "",
    },
  });

         const { user } = useTypedSelector(selectUserState);

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
    resetField,
    getValues,
    control,
    setError,
    formState: { errors: formErrors },
  } = formMethods;

  const submit = (data: InputFields) => {
    const _data = {
      description: data.milestone,
      file: data.file,
      amountPayable: data.amount,
      job_detail_id: jobId,
      start_date: data.start_date,
      duration: data.duration,
    };
    onAdd(_data);
    onClose();

    // setTableData((prev) => sortTimelines(prev.concat([_data])));

    // reset();
  };

  return (
    <Dialog.Root
      motionPreset="slide-in-bottom"
      open={open}
      onOpenChange={({ open }) => !open && onClose()}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content as="form" onSubmit={handleSubmit(submit)}>
            <Dialog.Header>
              <Dialog.Title fontSize={"lg"}>
                {edit ? "Update" : "Add"} Milestone
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body color="fg" spaceY={4}>
              <Field
                label="Milestone"
                htmlFor={FieldsName.MILESTONE}
                errorText={formErrors[FieldsName.MILESTONE]?.message}
              >
                <Input
                  id={FieldsName.MILESTONE}
                  {...register(FieldsName.MILESTONE)}
                  placeholder="Describe task"
                  borderWidth={"thin"}
                  p={2}
                />
              </Field>


                              <Controller
                                control={control}
                                name={FieldsName.FILE as any}
                                // rules={{ required: "Upload your job description" }}
                                render={({
                                  field: { name, value, onChange },
                                  fieldState: { error },
                                }) => {
                                  return (
                                    <Field label="Milestone job description" htmlFor={FieldsName.FILE} errorText={error?.message}>
                          

                                      <FileDropZone
                                    height="20px"
                                        inputId={FieldsName.FILE}
                                        values={value ? [value] : []}
                                        onChange={(files, err) => {
                                          if (!err) {
                                            onChange(files[0]);
                                            setError(name, {
                                              message: undefined,
                                              type: undefined,
                                            });
                                            return;
                                          }
                                          setError(name, { message: err, type: "" });
                                        }}
                                        limit={1}
                                        label={!value ? "Upload Milestone Job Description" : "Replace File"}
                                        accept={".pdf,.doc,.docx,.txt"}
                                      />
                            
                                    </Field>
                                  );
                                }}
                              />

              <HStack
                alignItems={"flex-start"}
                justifyContent="stretch"
                gap={4}
              >
                <Field
                  label="Amount"
                  htmlFor={FieldsName.AMOUNT}
                  errorText={formErrors[FieldsName.AMOUNT]?.message}
                >

                  
                  <Input
                    id={FieldsName.AMOUNT}
                    {...register(FieldsName.AMOUNT, {
                      required: "Amount is required",
                      min: 1,
                    })}
                    min={1}
                    placeholder={ user?.userCompany?.currency_symbol ===  '₦' ?`${ user?.userCompany?.currency_symbol  ?? '₦'}1000` : `${ user?.userCompany?.currency_symbol  ?? '$'}10`}
                    type="number"
                    borderWidth={"thin"}
                    p={2}
                  />
                </Field>

                <Field
                  label="Start Date"
                  htmlFor={FieldsName.START_DATE}
                  errorText={formErrors[FieldsName.START_DATE]?.message}
                >
                  <Input
                    id={FieldsName.START_DATE}
                    {...register(FieldsName.START_DATE, {
                      required: "Start date is required",
                    })}
                    onChange={(e) => {
                      // let date = moment(e.target.valueAsDate).format("YYYY-MM-DD");
                      // setMinDate(date), resetField(FieldsName.DURATION);
                    }}
                    min={minDate}
                    type="date"
                    borderWidth={"thin"}
                    p={2}
                  />
                </Field>
              </HStack>

              <Field
                label="Milestone duration (days)"
                htmlFor={FieldsName.DURATION}
                errorText={formErrors[FieldsName.DURATION]?.message}
                flex={1}
              >
                <Input
                  id={FieldsName.DURATION}
                  {...register(FieldsName.DURATION, {
                    required: "Duration is required",
                  })}
                  placeholder="...in days"
                  type="number"
                  borderWidth={"thin"}
                  p={2}
                />
              </Field>
            </Dialog.Body>

            <Dialog.Footer mt={8}>
              <Dialog.ActionTrigger asChild>
                <Button
                  color={"fg"}
                  variant={"outline"}
                  borderWidth={"thin"}
                  px={4}
                  // {/* type="submit"
                  // // className="w-full bg-brand-navy inline-flex items-center justify-center text-center text-white py-2 px-5 rounded-lg font-semibold cursor-pointer hover:bg-slate-700 hover:text-white transition duration-200
                  // // ease-in-out" */}
                >
                  Close
                </Button>
              </Dialog.ActionTrigger>
              <Button
                bg="primary"
                color="white"
                _hover={{ bg: "primary/90" }}
                type="submit"
                px={8}
                // {/* type="submit"
                // // className="w-full bg-brand-navy inline-flex items-center justify-center text-center text-white py-2 px-5 rounded-lg font-semibold cursor-pointer hover:bg-slate-700 hover:text-white transition duration-200
                // // ease-in-out" */}
              >
                Add
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton variant="ghost" color="fg" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
